import CardCommon from '@op/components/CardCommon';
import React, { useEffect, useMemo, useState } from 'react';
import {
  IOverviewMetrics,
  IRealtimeMentionsType,
  IRealtimeOpinionsTabType,
} from '../../../type';
import useLanguage from '@op/hooks/useLanguage';
import {
  REALTIME_METRICS_CARD_OPTIONS,
  REALTIME_TAB_OPTIONS,
} from '../../../constant';
import MetricsCard from '../../../component/MetricsCard';
import './index.scss';
import { Segment, TabPanel } from 'tea-component';
import LinkTab from '@op/components/LinkTab';
import CommonTitle from '../CommonTitle';
import { DBO_TM } from '@op/utils/format';
import { useRealtimeOverviewMetricsStats } from '../../../hook';
import { formatDateFunc } from '@op/utils/time';
import MentionsChart from '../../../component/MentionsChart';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

const OverviewMetrics = (props: IOverviewMetrics) => {
  const { filter, gameInfo, value, setValue, setEndTime } = props;
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [activeTabPanel, setActiveTabPanel] =
    useState<IRealtimeMentionsType>('mentions');
  const [byType, setByType] = useState('in_total');
  const isToday = useMemo(() => DBO_TM.isToday(filter), [filter]);

  const cardOptions = useMemo(() => {
    if (isToday) {
      return REALTIME_METRICS_CARD_OPTIONS.is_today;
    }
    return REALTIME_METRICS_CARD_OPTIONS.default;
  }, [isToday]);

  useEffect(() => {
    setByType('in_total');
    setActiveTabPanel(cardOptions[0].valueKey);
  }, [cardOptions]);

  const { dropdown, tabType } = useMemo(() => {
    const { dropdown, valueKey, tabKey } =
      cardOptions?.find((val) => val.valueKey === activeTabPanel) || {};
    return {
      dropdown,
      tabType: tabKey || valueKey,
    };
  }, [cardOptions, activeTabPanel]);
  const filterParams = useMemo(() => {
    if (isToday) {
      // 今天 need_many_hour_ago:true
      return {
        ...filter,
        ...gameInfo,
        date_type: 'hourly',
        need_many_hour_ago: isToday,
        many_hour_ago: value === 'last_hour' ? 1 : 0,
        tab_type: tabType,
        ...(dropdown ? { by_type: byType } : {}),
      };
    }
    return {
      ...filter,
      ...gameInfo,
      date_type: 'hourly',
      need_many_hour_ago: false,
      tab_type: tabType,
      ...(dropdown ? { by_type: byType } : {}),
    };
  }, [isToday, filter, gameInfo, value, dropdown, byType, tabType]);

  const { data, chartData, loading, refetch } = useRealtimeOverviewMetricsStats(
    {
      filter: filterParams,
      extraData: {
        metricsKey: cardOptions.map((val) => ({
          ...val,
          value: val.valueKey,
        })),
      },
    },
  );

  const { topList, newTime, newHourTime, oldTime, oldHourTime, endTime } =
    useMemo(() => {
      const { start_time, end_time, hour_link_of_time, day_link_of_time } =
        data || {};
      setEndTime(end_time);
      return {
        ...data,
        newTime: [start_time, end_time],
        newHourTime: [
          hour_link_of_time?.start_time,
          hour_link_of_time?.end_time,
        ],
        oldTime: [
          day_link_of_time?.link_of_start_time,
          day_link_of_time?.link_of_end_time,
        ],
        oldHourTime: [
          hour_link_of_time?.link_of_start_time,
          hour_link_of_time?.link_of_end_time,
        ],
        endTime: end_time,
      };
    }, [data]);

  return (
    <CardCommon
      title={
        <CommonTitle
          className={isToday ? 'overview-metrics-header-box' : ''}
          title={'Overview Metrics'}
          endTime={endTime}
        >
          {isToday && (
            <Segment
              className="dbo-realtime-select segment-light segment-font12"
              value={value}
              onChange={(value: IRealtimeOpinionsTabType) => {
                setValue(value);
              }}
              options={formatOptionsMessage(REALTIME_TAB_OPTIONS, undefined, {
                placement: 'top',
                style: { maxWidth: 220 },
              })}
            />
          )}
        </CommonTitle>
      }
      className="dbo-mentions-overview-wrapper"
      contentCard
    >
      <LinkTab
        activeId={activeTabPanel}
        tabs={cardOptions.map((item, index) => {
          const { isHourly } = item;
          const title = formatMessage({ id: item.title });
          return {
            label: (
              <CardCommon
                // loading={loading}
                key={index}
                loadingHeight={160}
                contentCard
              >
                <MetricsCard
                  data={{
                    ...topList,
                    time: isHourly ? newHourTime : newTime,
                    oldTime: isHourly ? oldHourTime : oldTime,
                  }}
                  tabItem={{
                    ...item,
                    title,
                    formatDate: (value: string) =>
                      formatDateFunc('hourly')(value),
                  }}
                />
              </CardCommon>
            ),
            id: item.valueKey,
          };
        })}
        onActive={(tab: any) => {
          if (isToday) {
            refetch(true);
          }
          setActiveTabPanel(tab.id);
          setByType('in_total');
          buriedBtnPoint('RO_OverviewMetrics_Tab', tab.id);
        }}
      >
        {cardOptions.map((item, index) => {
          const { tabKey, valueKey, ...restItem } = item || {};
          return (
            <TabPanel key={valueKey + index + isToday} id={valueKey}>
              <MentionsChart
                filter={filterParams}
                gameInfo={gameInfo}
                tabType={tabKey || valueKey}
                type={valueKey}
                key={valueKey + isToday + index}
                data={chartData}
                byType={byType}
                setByType={setByType}
                loading={loading}
                {...restItem}
              />
            </TabPanel>
          );
        })}
      </LinkTab>
    </CardCommon>
  );
};

export default OverviewMetrics;
